import { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAppDispatch } from '@/store/hooks';
import { setSession, setError } from '@/store/authSlice';
import { authApi } from '@/api/auth';
import { friendlyError } from '@/lib/errorMessage';

const GOOGLE_CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined) ?? '';
const GOOGLE_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';
const STATE_KEY = 'ssp_google_oauth_state';

function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getRedirectUri(): string {
  return `${window.location.origin}/login`;
}

function buildGoogleAuthUrl(state: string): string {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: getRedirectUri(),
    response_type: 'code',
    scope: 'openid email profile',
    state,
    access_type: 'online',
    prompt: 'select_account',
  });
  return `${GOOGLE_AUTH_URL}?${params.toString()}`;
}

// Google "G" logo SVG (monochrome for SSP style)
function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function GoogleLogin() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setLocalError] = useState<string | null>(null);
  const processedRef = useRef(false);

  const isConfigured = Boolean(GOOGLE_CLIENT_ID);

  // Handle the OAuth callback (code in URL)
  const handleCallback = useCallback(
    async (code: string) => {
      setIsLoading(true);
      setLocalError(null);

      try {
        const loginResponse = await authApi.loginWithGoogle(code, getRedirectUri());

        dispatch(
          setSession({
            sessionToken: loginResponse.sessionToken,
            user: loginResponse.user,
            expiresAt: loginResponse.expiresAt,
            loginMethod: 'google',
          })
        );
      } catch (err) {
        const msg = friendlyError(err, t, 'login.google.signInFailed');
        setLocalError(msg);
        dispatch(setError(msg));
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, t]
  );

  // Process OAuth callback on mount
  useEffect(() => {
    if (processedRef.current) return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    if (!code && !errorParam) return;

    processedRef.current = true;

    // Clean URL params
    setSearchParams({}, { replace: true });

    if (errorParam) {
      setLocalError(
        errorParam === 'access_denied'
          ? t('login.google.cancelled')
          : t('login.google.error', { error: errorParam })
      );
      return;
    }

    // Verify state to prevent CSRF
    const savedState = sessionStorage.getItem(STATE_KEY);
    sessionStorage.removeItem(STATE_KEY);

    if (!code || !state || state !== savedState) {
      setLocalError(t('login.google.invalidState'));
      return;
    }

    void handleCallback(code);
  }, [searchParams, setSearchParams, handleCallback, t]);

  const handleGoogleRedirect = () => {
    const state = generateState();
    sessionStorage.setItem(STATE_KEY, state);
    window.location.href = buildGoogleAuthUrl(state);
  };

  if (!isConfigured) {
    return (
      <div className="status-container">
        <h2 className="status-title">{t('login.google.notConfiguredTitle')}</h2>
        <p className="status-description">{t('login.google.notConfiguredDesc')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="login-method-divider" />
      <div className="status-container">
        <h2 className="status-title">
          {isLoading ? t('login.google.signingIn') : t('login.google.title')}
        </h2>
        <p className="status-description">
          {isLoading ? t('login.google.verifyingAccount') : t('login.google.subtitle')}
        </p>

        {error && <div className="error-message">{error}</div>}

        {isLoading && <div className="spinner" />}

        {!isLoading && (
          <button className="btn-primary" onClick={handleGoogleRedirect}>
            <GoogleLogo />
            {t('login.google.continueWithGoogle')}
          </button>
        )}
      </div>
    </>
  );
}

export default GoogleLogin;
